'use client';

/**
 * Client-side PostHog initialization — cross-cutting concern used on both
 * public pages (landing, commands, login — anonymous capture) and the
 * authenticated dashboard tree (identified capture via `userId` prop).
 * Mounts once on first page load; subsequent navigations keep the same
 * instance (posthog-js is a singleton).
 *
 * Design:
 *   - When `userId` is passed, calls `posthog.identify(userId)` so
 *     client-side events cross-reference with the server-side `trackEvent`
 *     calls (both use `User.id` as `distinctId`). posthog-js automatically
 *     aliases any prior anonymous distinct_id's events to the identified
 *     user on the first identify() of the session, enabling the landing →
 *     signup_completed funnel. Aliasing depends on the anonymous
 *     distinct_id surviving the OAuth redirect (same-origin localStorage +
 *     cookie persistence on shooshtts.com).
 *   - When `userId` is absent (public pages), PostHog is initialized without
 *     identify. Events still fire under an auto-generated distinct_id that
 *     gets merged into the user on the first post-signin `identify()`.
 *   - `person_profiles: 'identified_only'` means anonymous events fire but
 *     no person profile is created until identify() runs. This is the data-
 *     minimization default in posthog-js 1.97+ and does NOT break
 *     attribution — the anonymous events get retroactively associated with
 *     the identified person on the first identify() call.
 *   - Degrades to no-op when `NEXT_PUBLIC_POSTHOG_KEY` is unset — keeps dev
 *     functional without credentials, and a production misconfiguration
 *     won't crash the render.
 *   - Uses explicit `posthog.capture` calls for custom events
 *     (overlay_url_copied, upgrade_clicked) plus autocapture for pageviews,
 *     clicks, form submits, and web vitals.
 *
 * Scope exclusions: `/overlay/[userId]` is deliberately NOT wrapped —
 * it loads inside OBS Browser Source in headless Chromium, and session
 * replay would capture viewer chat content rendered on the overlay.
 */
import { useEffect } from 'react';
import posthog from 'posthog-js';
// Consent cookie read goes through the shared helper in consent-banner.js
// so the regex + cookie-name are single-sourced. No circular risk: the
// banner only imports posthog-js, React, and next/link — none re-import
// this module.
import { readConsent } from './components/consent-banner';

// Env discriminator — same value used server-side in lib/analytics.js, so
// dev/prod events can be filtered side-by-side in PostHog dashboards.
// NEXT_PUBLIC_APP_ENV lets staging override NODE_ENV explicitly.
const ENV_TAG = process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || 'development';

let initialized = false;
let globalListenersWired = false;

export function initPostHog() {
  if (initialized || typeof window === 'undefined') return posthog;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return posthog;
  try {
    // Consent gating — default opt-out unless cookie says accepted. The
    // initial $pageview fires as part of posthog.init, so the opt-out
    // default here is what prevents an unconsented capture. The banner
    // later calls opt_in_capturing() on Accept, and the dashboard
    // provider opt-ins authenticated users regardless (service relationship).
    const consent = readConsent();
    const optOutByDefault = consent !== 'accepted';

    posthog.init(key, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      capture_pageview: true,
      capture_pageleave: true,
      person_profiles: 'identified_only',
      disable_session_recording: false,
      opt_out_capturing_by_default: optOutByDefault,
      // Mask EVERY <input> and <textarea> value in session replays. The
      // dashboard's support-modal free-text description + filter/pronunciation
      // inputs are user-authored content we don't want to replicate into a
      // third-party analytics service. PostHog still captures DOM structure
      // + element layout for replay — only the values are masked.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
      },
    });
    // Attach env as a super-property so every autocapture + custom client
    // event carries it. PostHog dashboards can then filter dev vs prod.
    posthog.register({ env: ENV_TAG });
    // Global handlers for errors that escape React's render tree — async
    // failures in event handlers, fetch rejections, etc. Next.js's error.js
    // boundaries catch render errors; these catch the rest.
    if (!globalListenersWired) {
      window.addEventListener('error', (evt) => {
        try {
          posthog.captureException(evt.error || new Error(evt.message), {
            error_boundary: 'window_onerror',
            filename: evt.filename,
            line: evt.lineno,
            col: evt.colno,
          });
        } catch {
          // Silent — already crashing.
        }
      });
      window.addEventListener('unhandledrejection', (evt) => {
        try {
          const reason = evt.reason instanceof Error ? evt.reason : new Error(String(evt.reason));
          posthog.captureException(reason, {
            error_boundary: 'unhandled_rejection',
          });
        } catch {
          // Silent.
        }
      });
      globalListenersWired = true;
    }
    initialized = true;
  } catch (err) {
    // Analytics failures must never block the app — log and continue.
    console.error('[posthog] init failed', err);
  }
  return posthog;
}

export function trackClientEvent(event, props = {}) {
  try {
    if (!initialized) return;
    posthog.capture(event, props);
  } catch {
    // Swallow — never let analytics break the UI.
  }
}

export default function PostHogProvider({ userId, children }) {
  useEffect(() => {
    const ph = initPostHog();
    if (!ph || !userId) return;
    try {
      // Authenticated users are auto-opted-in regardless of the public-page
      // consent cookie — we have a service relationship, analytics on the
      // authenticated product experience is operational (product usage,
      // billing funnel, error tracking). The privacy policy documents this.
      // Anonymous visitors on public pages still respect the banner.
      ph.opt_in_capturing();
      // identify() is idempotent-ish in posthog-js; the internal check is
      // cheap, and we want the mapping re-asserted on every session in case
      // localStorage was cleared.
      ph.identify(userId);
    } catch {
      // Ignore — identify failure shouldn't crash the app.
    }
  }, [userId]);

  return children;
}
